/**
 * A triangular brilliant — the stone that sits in each cell.
 *
 * Cut to the reference study's "straight variation": flat girdle edges rather
 * than bowed ones, four segments an edge, a broad table and a deep pavilion.
 *
 * Built once, in a canonical frame, and shared by every cell: an equilateral
 * outline of side 1 centred on the origin, apex toward +Y, crown toward +Z.
 * Cells place it by rotation alone, so `+Z is the crown` becomes `+Z is the
 * face's outward normal`.
 *
 * Depths are measured from the bottom of the girdle at z = 0, so a stone can be
 * dropped onto a face and sit like a set stone: crown and girdle proud of the
 * surface, pavilion buried in the solid beneath.
 *
 *        table                z = +0.23
 *      \_______/
 *       \     /   crown
 *        \___/               z = +0.08   girdle top
 *        |   |    girdle          <- widest point
 *        |___|               z =  0.00   girdle bottom
 *        \   /    break
 *         \ /     pavilion
 *          v                 z = -0.39   culet
 *
 * The girdle must be the widest point. The reference sets its break ring at
 * 1.03x the girdle, so the stone flares below its widest point before tapering
 * and reads as a mushroom; every ring below the girdle here is strictly inside
 * it, and a test asserts the profile.
 */

import {
  BufferGeometry,
  DataTexture,
  Float32BufferAttribute,
  NearestFilter,
  NoColorSpace,
  RGBAFormat,
  Vector2,
  Vector3,
} from 'three/webgpu';

/** Circumradius of an equilateral triangle of side 1. */
const UNIT_RADIUS = 1 / Math.sqrt(3);

const DEFAULTS = {
  /** Points per outline edge. */
  edgeSegments: 4,
  /** Straight variation: no bow on the girdle edges. */
  bulge: 0,
  /** Outline size, as a fraction of the cell it sits in. */
  spread: 0.94,

  /** Crown. Proportions carried over from the reference's straight variation. */
  girdleTop: 0.15,
  crownHeight: 0.27,
  tableScale: 0.57,

  /**
   * Pavilion, in three tiers like the reference — break, inner, culet — but
   * every scale below 1 so the taper from the girdle is monotonic.
   */
  pavilionDepth: 0.72,
  breakScale: 0.8,
  breakDepth: 0.18,
  innerScale: 0.46,
  innerDepth: 0.64,
  culetScale: 0.07,
  culetDepth: 0.95,
};

/** The outline, sampled into `3 * segments` points around a triangle. */
function outline(segments, bulge, radius) {
  const corners = [0, 1, 2].map((i) => {
    const angle = Math.PI / 2 + (i * 2 * Math.PI) / 3;
    return new Vector2(Math.cos(angle) * radius, Math.sin(angle) * radius);
  });

  const points = [];
  for (let edge = 0; edge < 3; edge += 1) {
    const from = corners[edge];
    const to = corners[(edge + 1) % 3];
    const outward = from.clone().add(to).multiplyScalar(0.5).normalize();

    for (let step = 0; step < segments; step += 1) {
      const t = step / segments;
      const p = from.clone().lerp(to, t);
      if (bulge) p.addScaledVector(outward, Math.sin(Math.PI * t) * bulge);
      points.push(p);
    }
  }
  return points;
}

const ring = (points, scale, z) =>
  points.map((p) => new Vector3(p.x * scale, p.y * scale, z));

/**
 * Build the shared gem geometry.
 *
 * Non-indexed, so `computeVertexNormals` gives every triangle its own normal
 * and the stone reads as faceted rather than smooth. Vertex colours carry a
 * per-facet brightness only — hue comes from the material, so one geometry
 * serves stones of any colour.
 */
export function buildGemGeometry(options = {}) {
  const o = { ...DEFAULTS, ...options };

  // Reference proportions are quoted against an outline of radius 1.
  const radius = UNIT_RADIUS * o.spread;
  const up = (v) => v * radius;

  const flat = outline(o.edgeSegments, o.bulge * radius, radius);
  const n = flat.length;

  const girdleTopZ = up(o.girdleTop);
  const tableZ = girdleTopZ + up(o.crownHeight);
  const depth = up(o.pavilionDepth);

  const table = ring(flat, o.tableScale, tableZ);
  const girdleTop = ring(flat, 1, girdleTopZ);
  const girdleBottom = ring(flat, 1, 0);
  const breakRing = ring(flat, o.breakScale, -depth * o.breakDepth);
  const innerRing = ring(flat, o.innerScale, -depth * o.innerDepth);
  const culet = ring(flat, o.culetScale, -depth * o.culetDepth);

  const tableCentre = new Vector3(0, 0, tableZ);
  const culetCentre = new Vector3(0, 0, -depth);
  const heart = new Vector3(0, 0, (tableZ - depth) / 2);

  const positions = [];
  const colors = [];
  const variance = [];
  /** Which facet each vertex belongs to, so UVs can be laid out once the count is known. */
  const facetOf = [];

  /**
   * Per-facet reflection character, as a pair in 0..1: how polished this facet
   * is, and where in the thin-film range its fire sits. The material spreads
   * them over its roughness and iridescence-thickness ranges, so no two facets
   * take the light quite the same way.
   *
   * Vertex colour alone gives every facet the same *surface* under a different
   * brightness, which is what makes an untreated stone read as flat shading on
   * a solid rather than as a cut. Real ones do not: a facet's polish, and the
   * depth of stone behind it, both vary, and the eye reads that variation as
   * the thing being transparent.
   *
   * Hashed off a running facet count so it is stable build to build, arbitrary
   * between neighbours — adjacent facets must not drift smoothly into each
   * other, or the variation reads as a gradient across the stone — and needs
   * nothing threaded through the call sites below.
   */
  let facetCount = 0;
  const hash = (seed) => {
    const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
    return x - Math.floor(x);
  };

  /**
   * Add a triangle, wound so it faces away from the middle of the stone. Left
   * to chance the winding comes out mixed, and a faceted solid with inverted
   * faces lights up wrong.
   */
  const facet = (a, b, c, shade) => {
    const u = new Vector3().subVectors(b, a);
    const v = new Vector3().subVectors(c, a);
    const normal = new Vector3().crossVectors(u, v);
    const mid = new Vector3().add(a).add(b).add(c).divideScalar(3).sub(heart);

    const wound = normal.dot(mid) < 0 ? [a, c, b] : [a, b, c];
    for (const p of wound) positions.push(p.x, p.y, p.z);

    const polish = hash(facetCount);
    const fire = hash(facetCount + 101.7);
    for (let i = 0; i < 3; i += 1) {
      colors.push(shade, shade, shade);
      variance.push(polish, fire);
      facetOf.push(facetCount);
    }
    facetCount += 1;
  };

  /**
   * Facet brightness. A wide swing on purpose: neighbouring facets want to read
   * light against dark, or the stone flattens into a pastel blob however well
   * it is lit.
   */
  const shadeAt = (i, tier) => 0.78 + Math.sin((i / n) * Math.PI * 6 + tier * 1.9) * 0.42;

  // Table.
  for (let i = 0; i < n; i += 1) {
    facet(tableCentre, table[i], table[(i + 1) % n], 1.15);
  }

  // Crown. The straight variation runs its diagonals one way throughout rather
  // than alternating, which keeps the facets reading as clean long triangles.
  for (let i = 0; i < n; i += 1) {
    const next = (i + 1) % n;
    facet(table[i], girdleTop[i], table[next], shadeAt(i, 1));
    facet(table[next], girdleTop[i], girdleTop[next], shadeAt(i, 2));
  }

  // Girdle: a straight band at the stone's widest point.
  for (let i = 0; i < n; i += 1) {
    const next = (i + 1) % n;
    const shade = shadeAt(i, 3) * 0.62;
    facet(girdleTop[i], girdleBottom[i], girdleBottom[next], shade);
    facet(girdleTop[i], girdleBottom[next], girdleTop[next], shade);
  }

  // Pavilion: girdle -> break -> inner -> culet, tapering the whole way.
  const tier = (from, to, base, alternate) => {
    for (let i = 0; i < n; i += 1) {
      const next = (i + 1) % n;
      const forward = !alternate || i % 2 === 0;
      if (forward) {
        facet(from[i], to[i], to[next], shadeAt(i, base) * 0.86);
        facet(from[i], to[next], from[next], shadeAt(i, base + 1) * 0.86);
      } else {
        facet(from[i], to[i], from[next], shadeAt(i, base + 1) * 0.86);
        facet(from[next], to[i], to[next], shadeAt(i, base) * 0.86);
      }
    }
  };

  tier(girdleBottom, breakRing, 4, false);
  tier(breakRing, innerRing, 6, true);
  tier(innerRing, culet, 8, true);

  // A small flat culet rather than a needle point.
  for (let i = 0; i < n; i += 1) {
    facet(culetCentre, culet[i], culet[(i + 1) % n], 0.55);
  }

  // One texel of the normal map per facet, in a square grid just big enough.
  const grid = Math.ceil(Math.sqrt(facetCount));
  const uvs = [];
  for (let v = 0; v < facetOf.length; v += 1) {
    const k = facetOf[v];
    const [cx, cy] = [k % grid, Math.floor(k / grid)];
    // A small triangle around the middle of the facet's own texel, rather than
    // three copies of the texel centre.
    //
    // A single point would be the obvious way to guarantee "one normal for the
    // whole facet", and it breaks the shader: with all three UVs equal the UV
    // derivatives are zero, and the tangent frame a normal map is expressed in
    // is degenerate — the stone renders black or NaN. A triangle a quarter of a
    // texel across is comfortably inside the texel, so nearest sampling still
    // returns that one texel everywhere on the facet, and the frame is
    // well-defined.
    const r = 0.25 / grid;
    const angle = (v % 3) * ((2 * Math.PI) / 3) + Math.PI / 2;
    uvs.push(
      (cx + 0.5) / grid + Math.cos(angle) * r,
      (cy + 0.5) / grid + Math.sin(angle) * r,
    );
  }

  // Tangents, supplied rather than derived. The UV triangles are tiny and the
  // winding flips facet to facet (see `facet`), which is exactly the case where
  // deriving a frame from screen-space derivatives gets the handedness wrong
  // and lights half the stone inside out.
  const tangents = [];
  for (let f = 0; f < facetCount; f += 1) {
    const i = f * 3;
    const p = [0, 1, 2].map((j) => new Vector3(...positions.slice((i + j) * 3, (i + j) * 3 + 3)));
    const uv = [0, 1, 2].map((j) => [uvs[(i + j) * 2], uvs[(i + j) * 2 + 1]]);

    const e1 = new Vector3().subVectors(p[1], p[0]);
    const e2 = new Vector3().subVectors(p[2], p[0]);
    const du1 = uv[1][0] - uv[0][0];
    const dv1 = uv[1][1] - uv[0][1];
    const du2 = uv[2][0] - uv[0][0];
    const dv2 = uv[2][1] - uv[0][1];

    const det = du1 * dv2 - du2 * dv1;
    const r = Math.abs(det) < 1e-12 ? 0 : 1 / det;
    const T = new Vector3()
      .copy(e1)
      .multiplyScalar(dv2 * r)
      .addScaledVector(e2, -dv1 * r);
    const B = new Vector3()
      .copy(e2)
      .multiplyScalar(du1 * r)
      .addScaledVector(e1, -du2 * r);

    const N = new Vector3().crossVectors(e1, e2).normalize();
    // Gram-Schmidt against the face normal, then handedness from the bitangent.
    T.addScaledVector(N, -N.dot(T));
    if (T.lengthSq() < 1e-16) T.set(1, 0, 0).addScaledVector(N, -N.x);
    T.normalize();
    const w = new Vector3().crossVectors(N, T).dot(B) < 0 ? -1 : 1;

    for (let j = 0; j < 3; j += 1) tangents.push(T.x, T.y, T.z, w);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
  geometry.setAttribute('facetVariance', new Float32BufferAttribute(variance, 2));
  geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  geometry.setAttribute('tangent', new Float32BufferAttribute(tangents, 4));
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  geometry.userData.facetCount = facetCount;
  geometry.userData.facetGrid = grid;
  return geometry;
}

/**
 * The normal map the UVs above address: one texel per facet, each a slightly
 * different tangent-space normal.
 *
 * This is what stops **coplanar facets reflecting identically**. The geometry
 * has plenty of them — the girdle band is a ring of quads split into pairs of
 * triangles, and each pavilion tier does the same — and flat shading gives
 * every triangle in a plane the exact same normal, so they mirror the same part
 * of the environment and fuse into one surface. A real stone does not do that:
 * no two facets are polished to quite the same plane, and the tiny disagreement
 * is what makes the light break up across the crown.
 *
 * Nearest sampling, no mipmaps, no anisotropy: every one of those is a way for
 * a neighbouring facet's normal to bleed in, and the whole point is that a
 * facet is flat and singular. `NoColorSpace` because these are vectors, not
 * colour — an sRGB decode would bend every one of them toward the pole.
 *
 * @param {number} grid texels per side, from `geometry.userData.facetGrid`
 * @param {number} tiltDegrees how far a facet may lean off its true normal
 */
export function buildGemFacetNormalMap(grid, tiltDegrees = 9) {
  const data = new Uint8Array(grid * grid * 4);
  const hash = (seed) => {
    const x = Math.sin(seed * 45.164 + 12.771) * 37193.891;
    return x - Math.floor(x);
  };

  const maxTilt = (tiltDegrees * Math.PI) / 180;
  for (let i = 0; i < grid * grid; i += 1) {
    // Uniform over the cap rather than uniform in angle, or the lean bunches
    // toward the pole and the variation stops reading at the rim.
    const azimuth = hash(i) * Math.PI * 2;
    const tilt = Math.acos(1 - hash(i + 57.3) * (1 - Math.cos(maxTilt)));
    const n = [
      Math.sin(tilt) * Math.cos(azimuth),
      Math.sin(tilt) * Math.sin(azimuth),
      Math.cos(tilt),
    ];
    for (let c = 0; c < 3; c += 1) {
      data[i * 4 + c] = Math.round((n[c] * 0.5 + 0.5) * 255);
    }
    data[i * 4 + 3] = 255;
  }

  const texture = new DataTexture(data, grid, grid, RGBAFormat);
  texture.magFilter = NearestFilter;
  texture.minFilter = NearestFilter;
  texture.generateMipmaps = false;
  texture.colorSpace = NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}
